import React from "react";
import RangeSlider from "react-range-slider-input";
import "react-range-slider-input/dist/style.css";
import Button from "../button/Button";

type ExportModalProps = {
    isOpen: boolean;
    onClose: () => void;
    onExport: () => void;
    totalItems: number;
    riskLevel: number;
    onRiskLevelChange: (level: number) => void;
    transactionsPerGroup: number;
    onTransactionsPerGroupChange: (count: number) => void;
};

const ExportModal = ({
    isOpen,
    onClose,
    onExport,
    totalItems,
    riskLevel,
    onRiskLevelChange,
    transactionsPerGroup,
    onTransactionsPerGroupChange,
}: ExportModalProps) => {
    if (!isOpen) return null;

    return (
        <div 
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
            onClick={onClose}
        >
            <div 
                className="z-50 bg-white rounded-xl p-8 max-w-md w-full mx-4 shadow-xl"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="flex flex-col gap-6">
                    {/* Header */}
                    <div>
                        <h2 className="text-charcoal font-bold text-xl mb-2">
                            Export Purchases
                        </h2>
                        <p className="text-muted text-sm">
                            Customize your export settings
                        </p>
                    </div>

                    {/* Risk Level Slider */}
                    <div className="flex flex-col gap-3">
                        <label className="text-charcoal font-medium text-sm">
                            Risk Level
                        </label>
                        <div className="flex flex-col gap-2 px-2">
                            <div className="relative">
                                <RangeSlider
                                    min={0}
                                    max={25}
                                    step={1}
                                    value={[riskLevel, 25]}
                                    onInput={(value: number[]) => onRiskLevelChange(value[0])}
                                />
                                {/* Value indicator below slider */}
                                <div 
                                    className="absolute -bottom-6 transform -right-6"
                                    style={{ left: `${(riskLevel / 25) * 100}%` }}
                                >
                                    <span className="text-primary-shade text-xs">
                                        {riskLevel}
                                    </span>
                                </div>
                            </div>
                            <div className="flex justify-between text-sm mt-6">
                                <span className="text-charcoal font-medium">Min: 0</span>
                                <span className="text-charcoal font-medium">Max: 25</span>
                            </div>
                        </div>
                    </div>

                    {/* Transactions per Group Input */}
                    <div className="flex flex-col gap-2">
                        <label className="text-charcoal font-medium text-sm">
                            Transactions per Group
                        </label>
                        <input
                            type="number"
                            value={transactionsPerGroup}
                            onChange={(e) => onTransactionsPerGroupChange(parseInt(e.target.value) || 250)}
                            min="1"
                            className="w-full px-4 py-3 border border-border rounded-lg text-charcoal bg-white focus:outline-none focus:ring-2 focus:ring-primary"
                        />
                    </div>

                    {/* Action Buttons */}
                    <div className="flex gap-3 mt-2">
                        <button
                            onClick={onClose}
                            className="flex-1 px-6 py-3 rounded-lg text-charcoal font-medium hover:bg-cloud-tint transition-colors"
                        >
                            Cancel
                        </button>
                        <Button
                            variant="primary"
                            size="medium"
                            onClick={onExport}
                            className="flex-1"
                            disabled={totalItems === 0}
                        >
                            {totalItems === 0 ? "No Items to Export" : `Export ${totalItems} ${totalItems === 1 ? "Item" : "Items"}`}
                        </Button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ExportModal;

